import { useRef, useEffect, useState, useCallback } from 'react';
import { globalEventEmitter } from './events.mjs';

/**
 * Subscribe to domain events and refetch on match (debounced).
 * @agentic @benchmark/core
 */ function useEventDrivenRefetch(eventNames, options) {
    const { refetch, enabled = true, filter, debounceMs = 200, emitter = globalEventEmitter } = options;
    // Keep the latest refetch/filter in refs so changing their identity
    // (the common case — inline arrow functions) does not tear down and
    // rebuild the subscription on every render.
    const refetchRef = useRef(refetch);
    const filterRef = useRef(filter);
    const debounceTimerRef = useRef(null);
    useEffect(()=>{
        refetchRef.current = refetch;
        filterRef.current = filter;
    });
    // A stable key over the event-name set: the subscribe effect only
    // re-runs when the *contents* change, not when the caller passes a new
    // array literal with the same names.
    const eventKey = eventNames.join("|");
    useEffect(()=>{
        if (!enabled || eventNames.length === 0) {
            return;
        }
        const handler = (payload)=>{
            if (filterRef.current && !filterRef.current(payload)) {
                return;
            }
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
            debounceTimerRef.current = setTimeout(()=>{
                debounceTimerRef.current = null;
                void Promise.resolve(refetchRef.current()).catch((err)=>{
                    console.error("useEventDrivenRefetch: refetch failed", err);
                });
            }, debounceMs);
        };
        const unsubscribes = eventNames.map((name)=>emitter.subscribe(name, handler));
        return ()=>{
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
                debounceTimerRef.current = null;
            }
            unsubscribes.forEach((unsubscribe)=>unsubscribe());
        };
    // `eventNames` is intentionally tracked via the derived `eventKey`
    // string rather than spread into the dependency array, which keeps
    // the subscription stable across array-literal identity churn.
    }, [
        enabled,
        debounceMs,
        emitter,
        eventKey
    ]);
}

/**
 * Fetch a resource with deps-driven + event-driven refresh.
 * @agentic @benchmark/core
 */ function useResourceFetcher(options) {
    const { fetch, deps, enabled = true, initialData, refetchOn, onError } = options;
    const [data, setData] = useState(initialData);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    // Track the latest fetch/onError via refs so their identity churn does
    // not re-run the deps effect (deps are the explicit invalidation key).
    const fetchRef = useRef(fetch);
    const onErrorRef = useRef(onError);
    useEffect(()=>{
        fetchRef.current = fetch;
        onErrorRef.current = onError;
    });
    const abortRef = useRef(null);
    const inFlightRef = useRef(null);
    // Core fetch routine: aborts any in-flight request, then runs a fresh
    // one. Only the controller that is still current may mutate state, so a
    // superseded (aborted) fetch can never clobber newer results.
    const doFetch = useCallback(()=>{
        abortRef.current?.abort();
        const controller = new AbortController();
        abortRef.current = controller;
        setLoading(true);
        setError(null);
        const promise = (async ()=>{
            try {
                const result = await fetchRef.current(controller.signal);
                if (!controller.signal.aborted) {
                    setData(result);
                }
            } catch (err) {
                if (!controller.signal.aborted) {
                    const normalized = err instanceof Error ? err : new Error(String(err));
                    setError(normalized);
                    onErrorRef.current?.(normalized);
                }
            } finally{
                if (!controller.signal.aborted) {
                    setLoading(false);
                }
            }
            if (abortRef.current === controller) {
                // Still the current request: clear the in-flight handle.
                inFlightRef.current = null;
            } else if (inFlightRef.current) {
                // Superseded by a newer fetch (e.g. deps changed mid-flight): chain
                // to it so callers awaiting this promise resolve on the freshest
                // data rather than on this aborted request.
                await inFlightRef.current;
            }
        })();
        inFlightRef.current = promise;
        return promise;
    }, []);
    // Public refetch dedupes concurrent callers onto the in-flight promise.
    const refetch = useCallback(()=>{
        if (!enabled) {
            return Promise.resolve();
        }
        if (inFlightRef.current) {
            return inFlightRef.current;
        }
        return doFetch();
    }, [
        enabled,
        doFetch
    ]);
    useEffect(()=>{
        if (!enabled) {
            // Aborting an in-flight request skips the `finally` loading reset, so
            // clear it here to avoid a spinner stuck on once the hook is disabled.
            abortRef.current?.abort();
            inFlightRef.current = null;
            setLoading(false);
            return;
        }
        void doFetch();
        return ()=>{
            abortRef.current?.abort();
        };
    // `deps` is the caller-provided invalidation key; spreading it is the
    // intended contract for this hook.
    }, [
        enabled,
        doFetch,
        ...deps
    ]);
    useEventDrivenRefetch(refetchOn?.events ?? [], {
        refetch,
        enabled: enabled && !!refetchOn,
        filter: refetchOn?.filter,
        debounceMs: refetchOn?.debounceMs
    });
    return {
        data,
        loading,
        error,
        refetch
    };
}

export { useEventDrivenRefetch, useResourceFetcher };
